import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaFileInvoice, FaChartPie } from "react-icons/fa";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import { apiService } from "../../../components/Config/api.service";
import AccountingJournal from "./AccountingJournal";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export default function Comptability({ month, year }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("journal"); // 'journal' | 'analytics'
    const [items, setItems] = useState([]); // { label, cost }
    const [uniqueDepartments, setUniqueDepartments] = useState(0);
    const [uniqueAssignments, setUniqueAssignments] = useState(0);
    const [employeeCount, setEmployeeCount] = useState(0);

    const [viewBy, setViewBy] = useState("department"); // 'department' | 'assignment'
    const [highlighted, setHighlighted] = useState(null);
    const [loadError, setLoadError] = useState(null);

    // Fetch repartition from backend when viewBy, month or year changes (or on mount)
    useEffect(() => {
        let cancelled = false;

        const endpoint = `accounting/repartition?view_by=${viewBy}${month ? `&month=${month}` : ""}`;

        apiService.getElement(endpoint)
            .then((response) => {
                if (cancelled) return;
                const data = response.data || response;
                setLoadError(null);
                setItems(Array.isArray(data.items) ? data.items : []);
                setUniqueDepartments(typeof data.unique_departments === "number" ? data.unique_departments : 0);
                setUniqueAssignments(typeof data.unique_assignments === "number" ? data.unique_assignments : 0);
                setEmployeeCount(typeof data.employee_count === "number" ? data.employee_count : 0);
            })
            .catch((error) => {
                if (cancelled) return;
                // L'erreur était avalée : l'onglet restait vide sans explication.
                setItems([]);
                setLoadError(error?.response?.data?.message || t('comptability.loadError'));
            });

        return () => (cancelled = true);
    }, [viewBy, month, year, t]);

    // Aggregate costs by key (department or assignment)
    const aggregated = useMemo(() => {
        const map = new Map();
        for (const e of items) {
            const key = e.label ?? (viewBy === "department" ? e.department : e.assignment) ?? "N/A";
            const cost = Number(e.cost) || 0;
            map.set(key, (map.get(key) || 0) + cost);
        }
        const aggrItems = Array.from(map.entries())
            .map(([label, cost]) => ({ label, cost }))
            .sort((a, b) => b.cost - a.cost);
        const total = aggrItems.reduce((s, it) => s + it.cost, 0);
        return { items: aggrItems, total };
    }, [items, viewBy]);

    const pieData = {
        labels: aggregated.items.map((i) => i.label),
        datasets: [
            {
                data: aggregated.items.map((i) => i.cost),
                backgroundColor: [
                    "#0C5B6D",
                    "#FB923C",
                    "#06B6D4",
                    "#EF4444",
                    "#10B981",
                    "#6366F1",
                    "#F97316",
                    "#93C5FD",
                ],
                hoverOffset: 8,
            },
        ],
    };

    const barData = {
        labels: aggregated.items.map((i) => i.label),
        datasets: [
            {
                label: t('comptability.cost'),
                data: aggregated.items.map((i) => i.cost),
                backgroundColor: aggregated.items.map((i, idx) =>
                    idx === 0 ? "#0C5B6D" : "#93C5FD"
                ),
            },
        ],
    };

    const pieOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "right", labels: { boxWidth: 12, padding: 8 } },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const value = ctx.parsed;
                        const total = aggregated.total || 1;
                        const pct = ((value / total) * 100).toFixed(1);
                        return `${ctx.label}: FCFA ${Number(value).toLocaleString()} (${pct}%)`;
                    },
                },
            },
        },
        onHover: (evt, elements) => {
            if (elements.length) {
                const idx = elements[0].index;
                setHighlighted(pieData.labels[idx]);
            } else {
                setHighlighted(null);
            }
        },
    };

    const barOptions = {
        maintainAspectRatio: false,
        indexAxis: "y",
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const v = (ctx.parsed && (ctx.parsed.x ?? ctx.parsed.y ?? ctx.parsed)) || 0;
                        return `FCFA ${Number(v).toLocaleString()}`;
                    }
                }
            },
        },
        scales: {
            x: { ticks: { callback: (v) => `FCFA ${Number(v).toLocaleString()}` } },
        },
        onHover: (evt, elements) => {
            if (elements && elements.length > 0) {
                const idx = elements[0].index;
                const label = aggregated.items[idx] ? aggregated.items[idx].label : null;
                setHighlighted(label);
            } else {
                setHighlighted(null);
            }
        }
    };

    const top = aggregated.items[0] || { label: "-", cost: 0 };

    return (
        <div className="w-full space-y-4">
            {/* Navigation par Onglets Principaux */}
            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm border">
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab("journal")}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
                            activeTab === "journal"
                                ? "bg-[--color-primary] text-white shadow"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                        <FaFileInvoice size={14} />
                        Journal Comptable (Écritures & Export)
                    </button>
                    <button
                        onClick={() => setActiveTab("analytics")}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
                            activeTab === "analytics"
                                ? "bg-[--color-primary] text-white shadow"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                        <FaChartPie size={14} />
                        Analytique & Répartition des Coûts
                    </button>
                </div>

                {activeTab === "analytics" && (
                    <div className="flex gap-2">
                        <button
                            onClick={() => setViewBy("department")}
                            className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                                viewBy === "department"
                                    ? "bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200"
                                    : "bg-gray-100 text-gray-700"
                            }`}
                        >
                            {t('accountingJournal.department')}
                        </button>
                        <button
                            onClick={() => setViewBy("assignment")}
                            className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                                viewBy === "assignment"
                                    ? "bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200"
                                    : "bg-gray-100 text-gray-700"
                            }`}
                        >
                            {t('accountingJournal.assignment')}
                        </button>
                    </div>
                )}
            </div>

            {/* Vue Onglet 1 : Journal Comptable Pleine Page */}
            {activeTab === "journal" && (
                <AccountingJournal
                    selectedMonth={month}
                    isInline={true}
                />
            )}

            {/* Vue Onglet 2 : Analytique & Graphiques */}
            {activeTab === "analytics" && loadError && (
                <div className="p-3 bg-red-50 border border-red-300 text-red-800 rounded-md text-sm" data-cy="repartition-error">
                    {loadError}
                </div>
            )}

            {activeTab === "analytics" && (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white shadow-sm rounded-lg border p-4 flex flex-col justify-center">
                            <div className="text-xs font-medium text-gray-500">{t('comptability.employees')}</div>
                            <div className="text-2xl font-bold text-gray-800">{employeeCount.toLocaleString()}</div>
                        </div>

                        <div className="bg-white shadow-sm rounded-lg border p-4 flex flex-col justify-center">
                            <div className="text-xs font-medium text-gray-500">{t('comptability.totalCost')}</div>
                            <div className="text-2xl font-bold text-gray-800">FCFA {aggregated.total.toLocaleString()}</div>
                        </div>

                        <div className="bg-white shadow-sm rounded-lg border p-4 flex flex-col justify-center">
                            <div className="text-xs font-medium text-gray-500">{viewBy === "department" ? t('accountingJournal.department') : t('accountingJournal.assignment')}</div>
                            <div className="text-2xl font-bold text-gray-800">{viewBy === "department" ? uniqueDepartments : uniqueAssignments}</div>
                        </div>

                        <div className="bg-white shadow-sm rounded-lg border p-4 flex flex-col justify-center">
                            <div className="text-xs font-medium text-gray-500">
                                {t('comptability.top', { view: viewBy === "department" ? t('accountingJournal.department') : t('accountingJournal.assignment') })}
                            </div>
                            <div className="text-xl font-bold text-[--color-primary]">{top.label}</div>
                            <div className="text-xs text-gray-600">FCFA {top.cost.toLocaleString()}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-stretch min-h-[520px]">
                        <div className="bg-white shadow-sm rounded-lg border p-4">
                            <h4 className="font-semibold text-sm mb-3 text-gray-800">{t('comptability.details')}</h4>
                            <div className="space-y-2 max-h-[450px] overflow-auto pr-1">
                                {aggregated.items.map((it) => {
                                    const pct = ((it.cost / aggregated.total) * 100 || 0).toFixed(1);
                                    const isHighlighted = highlighted === it.label;
                                    return (
                                        <div
                                            key={it.label}
                                            className={`flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer transition-all ${
                                                isHighlighted ? "bg-indigo-50 border border-indigo-200" : "border border-transparent"
                                            }`}
                                            onMouseEnter={() => setHighlighted(it.label)}
                                            onMouseLeave={() => setHighlighted(null)}
                                        >
                                            <div className="flex flex-col">
                                                <div className="text-xs font-medium text-gray-800">{it.label}</div>
                                                <div className="text-[11px] text-gray-500">{pct}%</div>
                                            </div>
                                            <div className="text-xs font-semibold text-gray-700">FCFA {it.cost.toLocaleString()}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="col-span-1 md:col-span-3 flex flex-col">
                            <div className="bg-white shadow-sm rounded-lg border p-4 h-full flex flex-col">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold text-sm text-gray-800">{t('comptability.distribution', { view: viewBy === "department" ? t('accountingJournal.department') : t('accountingJournal.assignment') })}</h3>
                                    <div className="text-xs text-gray-500">{t('comptability.hoverToHighlight')}</div>
                                </div>

                                <div className="flex flex-col md:flex-row gap-6 items-stretch flex-1">
                                    <div className="md:w-1/2 h-96 w-full">
                                        <Pie data={pieData} options={pieOptions} />
                                    </div>
                                    <div className="md:w-1/2 h-96 w-full">
                                        <Bar data={barData} options={barOptions} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
